import type { MetaFunction } from "@remix-run/node";

export const meta: MetaFunction = () => {
  return [
    { title: "家事紛争 - 国際離婚・相続・親権の専門弁護士 | 法務法人フィサン" },
    { name: "description", content: "韓国ソウルの家事事件専門弁護士。国際離婚、財産分与、慰謝料、養育費、親権、相続、遺産分割など家族問題をワンストップサポート。日韓の法律制度と文化の違いを理解し日本語で対応。" },
    { name: "keywords", content: "家事紛争, 国際離婚, 離婚, 財産分与, 慰謝料, 養育費, 親権, 相続, 遺産分割, 相続税, 贈与税, 韓国弁護士, 日本語対応, ソウル法律事務所" },
    { property: "og:title", content: "家事紛争 - 国際離婚・相続・親権の専門弁護士 | 法務法人フィサン" },
    { property: "og:description", content: "国際離婚、財産分与、親権、相続など家族問題の専門的なサポートを日本語で提供。日韓両国の法制度に精通した弁護士が対応します。" },
    { property: "og:type", content: "website" },
    { property: "og:url", content: "https://jp-hwisang.co.kr/ja/services/family" },
    { property: "og:image", content: "https://jp-hwisang.co.kr/images/jp-hwisang-card.jpg" },
    { property: "og:locale", content: "ja_JP" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "robots", content: "index, follow" },
  ];
};

export default function FamilyDetail() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
            <a href="/ja" className="flex items-center justify-center md:justify-start">
              <img src="/images/no-padding-logo.png" alt="法務法人フィサン" className="h-8 w-auto opacity-90" />
            </a>

            <div className="flex items-center justify-center md:justify-end gap-4 md:gap-6">
              <nav className="flex items-center gap-4 md:gap-6 text-sm">
                <a href="/ja/office" className="text-gray-700 hover:text-black whitespace-nowrap">事務所・所在地</a>
                <a href="/ja/services" className="text-black font-medium whitespace-nowrap">業務分野</a>
                <a href="/ja/blog" className="text-gray-700 hover:text-black whitespace-nowrap">法律ブログ</a>
              </nav>
              <a
                href="https://jp-hwisang.co.kr"
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-1.5 border border-gray-300 hover:border-gray-900 text-gray-700 hover:text-black text-sm transition-colors whitespace-nowrap"
              >
                한국어
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Back Button */}
      <section className="py-8 px-4 border-b border-gray-200">
        <div className="max-w-4xl mx-auto">
          <a href="/ja/services" className="inline-flex items-center text-sm text-gray-600 hover:text-black transition-colors">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            業務分野一覧に戻る
          </a>
        </div>
      </section>

      {/* Title Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-serif mb-6 text-gray-900">家事紛争</h1>
          <div className="w-16 h-px bg-gray-400 mb-8"></div>
          <p className="text-xl text-gray-700 leading-relaxed">
            離婚・相続など家族問題のためのワンストップサービス
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="pb-20 px-4">
        <div className="max-w-4xl mx-auto space-y-12">
          <div>
            <p className="text-base text-gray-700 leading-relaxed mb-6">
              家族に関する法律問題は、人生の中で最も個人的で繊細な問題の一つです。
            </p>
            <p className="text-base text-gray-700 leading-relaxed">
              輝相（フィサン）は、離婚・相続・親権・財産分割などの家事事件全般において、依頼者一人ひとりの事情に寄り添い、法的にも心理的にも支えるパートナーでありたいと考えています。
            </p>
          </div>

          <div className="border-t border-gray-200 pt-12">
            <h2 className="text-2xl font-serif mb-6 text-gray-900">国際離婚・財産分与</h2>
            <div className="space-y-4 text-base text-gray-700 leading-relaxed">
              <p>
                離婚に関するトラブルは、感情的な負担に加えて、財産分与・慰謝料・親権など、複雑な法的問題を伴います。
              </p>
              <p>
                特に、日本人と韓国人の国際離婚の場合には、どの国の法律を適用するか（準拠法）、どの裁判所に申し立てを行うか（管轄）の判断が必要になります。
              </p>
              <p>
                輝相（フィサン）は、日韓の法律制度と文化の違いを深く理解した弁護士が、依頼者の立場を的確に守り、韓国・日本いずれの手続きにも対応できるようサポートいたします。
              </p>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-12">
            <h2 className="text-2xl font-serif mb-6 text-gray-900">相続・遺産分割・相続税・贈与税の法的サポート</h2>
            <div className="space-y-4 text-base text-gray-700 leading-relaxed">
              <p>
                相続問題では、法的な手続きだけでなく、家族間の感情や信頼関係にも深く配慮することが求められます。
              </p>
              <p>
                輝相（フィサン）は、遺産分与・遺言の作成・相続放棄・税務問題（相続税・贈与税）などにおいて、専門的かつ実務的なアドバイスを提供します。
              </p>
              <p>
                また、日本に資産を持つ方や日本在住の家族が関係する国際相続にも対応し、両国の法制度と税制の違いを踏まえた最適な解決策を提案します。
              </p>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-12">
            <h2 className="text-2xl font-serif mb-6 text-gray-900">親権・養育権に関する紛争</h2>
            <div className="space-y-4 text-base text-gray-700 leading-relaxed">
              <p>
                離婚や別居後の親権・養育権に関しては、お子様の福祉を最優先に考えながら、双方の立場を尊重した形で解決を目指します。
              </p>
              <p>
                国際結婚における子どもの連れ去りや面会交流など、国際的な要素を含む案件にも経験豊富な弁護士が対応します。
              </p>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-12">
            <h2 className="text-2xl font-serif mb-6 text-gray-900">日韓の架け橋として</h2>
            <div className="space-y-4 text-base text-gray-700 leading-relaxed">
              <p>
                輝相（フィサン）は、日韓両国の法律・文化・言語を理解する法務チームとして、日本のクライアントの不安を軽減し、透明性と誠実さをもって解決まで導きます。
              </p>
              <p>
                法律問題を抱えたときこそ、冷静で信頼できるパートナーが必要です。私たちは、あなたの人生を守る味方として、確かな専門性と温かいサポートをお約束します。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-lg text-gray-700 mb-8 leading-relaxed">
            家事紛争でお困りの方は、<br />お気軽にご相談ください。
          </p>
          <div className="space-y-4">
            <a href="tel:010-4729-3573" className="block text-gray-900 hover:text-gray-600 transition-colors">
              <p className="text-lg font-medium">010-4729-3573</p>
            </a>
            <a href="mailto:mgkim@hwisang.co.kr" className="block text-gray-900 hover:text-gray-600 transition-colors">
              <p className="text-lg font-medium">mgkim@hwisang.co.kr</p>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center md:text-left">
            <div>
              <h5 className="text-base font-medium mb-4 text-gray-900">法務法人フィサン</h5>
              <p className="text-sm text-gray-600 leading-relaxed">
                韓国を代表する法律事務所として、<br />
                専門的で信頼できる法律サービスを提供します
              </p>
            </div>
            <div>
              <h5 className="text-base font-medium mb-4 text-gray-900">業務分野</h5>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>刑事事件</li>
                <li>金融・不動産</li>
                <li>経営権紛争</li>
                <li>家事事件</li>
              </ul>
            </div>
            <div>
              <h5 className="text-base font-medium mb-4 text-gray-900">お問い合わせ</h5>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>電話: 010-4729-3573</li>
                <li>Email: mgkim@hwisang.co.kr</li>
                <li>住所: ソウル特別市江南区永東大路611, 13階</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-200 mt-8 pt-8 text-center">
            <p className="text-xs text-gray-500">&copy; 2024 法務法人フィサン. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
